import { useTranslation } from "react-i18next"

import { Slider } from "@/components/ui/slider"
import { cn } from "@/lib/utils"

import { effortLabel } from "../utils/effort-label"

interface EffortSliderProps {
  /** The ordered ladder of effort stops this control offers (e.g. ["none", "low", "medium"]). */
  efforts: string[]
  /** The currently active effort. If not in `efforts`, the slider falls back to the first stop. */
  value: string
  onChange: (effort: string) => void
  disabled?: boolean
  /** Keyboard hint rendered as a kbd chip next to the label (e.g. "Alt +/−").
   *  Only the chat pill passes it — the admin dialog has no shortcut. */
  shortcutHint?: string
}

/**
 * Presentational reasoning-effort control: a labeled slider snapping to discrete
 * stops, with the active stop highlighted below. Shared by the chat reasoning-mode
 * pill and the admin fast-router dialog so both present an identical control.
 */
export function EffortSlider({
  efforts,
  value,
  onChange,
  disabled = false,
  shortcutHint,
}: EffortSliderProps) {
  const { t } = useTranslation()
  const activeIndex = Math.max(0, efforts.indexOf(value))

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase">
          {t("reasoning_mode.effort_label", { defaultValue: "Reasoning effort" })}
          {shortcutHint && (
            <kbd className="text-muted-foreground/70 border-border rounded border px-1 font-sans text-[10px] normal-case">
              {shortcutHint}
            </kbd>
          )}
        </span>
        <span className="text-primary text-xs font-semibold">
          {value ? effortLabel(value, t) : ""}
        </span>
      </div>
      <Slider
        aria-label={t("reasoning_mode.effort_label", { defaultValue: "Reasoning effort" })}
        disabled={disabled}
        max={efforts.length - 1}
        min={0}
        step={1}
        value={[activeIndex]}
        onValueChange={(v) => {
          const next = efforts[v[0]]
          if (next) onChange(next)
        }}
      />
      <div className="text-muted-foreground mt-2 flex justify-between text-[10px] font-medium">
        {efforts.map((effort, idx) => (
          <span
            key={effort}
            className={cn("whitespace-nowrap", idx === activeIndex && "text-foreground font-bold")}
          >
            {effortLabel(effort, t)}
          </span>
        ))}
      </div>
    </div>
  )
}
